
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'vpn-service', 'pricing-contact', 'support'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/e1cd9872-53a1-4a52-95de-ce140d99ae94.png" 
                alt="LA MADERERA S.R.L." 
                className="h-12 w-auto"
              />
              <span className="text-xl font-bold">LA MADERERA</span>
            </div>
            <div className="flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === 'home' ? 'text-blue-500' : 'text-gray-300 hover:text-white'
                }`}
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === 'about' ? 'text-blue-500' : 'text-gray-300 hover:text-white'
                }`}
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('vpn-service')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === 'vpn-service' ? 'text-blue-500' : 'text-gray-300 hover:text-white'
                }`}
              >
                VPN Service
              </button>
              <button 
                onClick={() => scrollToSection('pricing-contact')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === 'pricing-contact' ? 'text-blue-500' : 'text-gray-300 hover:text-white'
                }`}
              >
                Pricing & Contact
              </button>
              <button 
                onClick={() => scrollToSection('support')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSection === 'support' ? 'text-blue-500' : 'text-gray-300 hover:text-white'
                }`}
              >
                Support
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen relative overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0E27] via-[#0F1535] to-[#1A0B2E]"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-start justify-between gap-12 mb-16">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-8">
                <i className="ri-shield-check-line text-blue-500"></i>
                <span className="text-sm font-medium">Enterprise Security</span>
              </div>
              <h1 className="text-7xl font-bold leading-tight mb-8">
                Secure Your<br />
                Digital<br />
                Freedom
              </h1>
            </div>
            <div className="flex-1 pt-8">
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Protect your online privacy with military-grade encryption. Browse securely, access content freely, and keep your data safe from prying eyes with our advanced VPN technology.
              </p>
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => scrollToSection('pricing-contact')}
                  className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-2xl transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50 whitespace-nowrap cursor-pointer"
                >
                  Get Started
                </button>
                <button 
                  onClick={() => scrollToSection('vpn-service')}
                  className="px-8 py-4 border-2 border-white text-white font-semibold rounded-2xl hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>

          <div className="relative h-96 rounded-3xl overflow-hidden">
            <img 
              src="https://public.readdy.ai/ai/img_res/853fe5dd47a713ab7536037030712577.jpg"
              alt="VPN Network"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 via-transparent to-transparent"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#12162D]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400 tracking-widest uppercase mb-6">WHO WE ARE</p>
          <h2 className="text-6xl font-serif leading-tight mb-20" style={{ fontFamily: 'Playfair Display, serif' }}>
            Building Digital Privacy<br />
            Solutions for Modern<br />
            Businesses
          </h2>
          
          <div className="space-y-8 text-xl text-gray-400 leading-relaxed mb-24">
            <p>
              LA MADERERA S.R.L. is dedicated to protecting your digital freedom. We develop cutting-edge VPN technology that ensures your online activities remain private, secure, and unrestricted. Our mission is to make internet privacy accessible to everyone.
            </p>
            <p>
              With a focus on innovation and user experience, we combine advanced encryption protocols with lightning-fast servers to deliver a seamless and secure browsing experience. Your privacy is our priority.
            </p>
          </div>

          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="w-44 h-52 bg-[#1A1F3A] rounded-3xl border border-white/10 flex flex-col items-center justify-center gap-6 hover:border-blue-500/50 transition-all duration-300">
              <i className="ri-lock-line text-5xl text-blue-500"></i>
              <p className="text-base font-medium">256-bit Encryption</p>
            </div>
            <div className="w-44 h-52 bg-[#1A1F3A] rounded-3xl border border-white/10 flex flex-col items-center justify-center gap-6 hover:border-blue-500/50 transition-all duration-300">
              <i className="ri-eye-off-line text-5xl text-blue-500"></i>
              <p className="text-base font-medium">No-Log Policy</p>
            </div>
            <div className="w-44 h-52 bg-[#1A1F3A] rounded-3xl border border-white/10 flex flex-col items-center justify-center gap-6 hover:border-blue-500/50 transition-all duration-300">
              <i className="ri-global-line text-5xl text-blue-500"></i>
              <p className="text-base font-medium">Global Servers</p>
            </div>
            <div className="w-44 h-52 bg-[#1A1F3A] rounded-3xl border border-white/10 flex flex-col items-center justify-center gap-6 hover:border-blue-500/50 transition-all duration-300">
              <i className="ri-shield-flash-line text-5xl text-blue-500"></i>
              <p className="text-base font-medium">Kill Switch</p>
            </div>
            <div className="w-44 h-52 bg-[#1A1F3A] rounded-3xl border border-white/10 flex flex-col items-center justify-center gap-6 hover:border-blue-500/50 transition-all duration-300">
              <i className="ri-time-line text-5xl text-blue-500"></i>
              <p className="text-base font-medium">24/7 Protection</p>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Features */}
      <section id="vpn-service" className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-24">
            <p className="text-sm text-red-500 tracking-widest uppercase mb-4">FEATURES</p>
            <h2 className="text-6xl font-bold">
              Why Choose Our<br />
              VPN Service
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div className="bg-[#1C1C1E] rounded-3xl p-10 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-shield-keyhole-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Military-Grade Encryption</h3>
              <p className="text-base text-gray-400 leading-relaxed">
                Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide to safeguard classified information.
              </p>
            </div>

            <div className="bg-[#1C1C1E] rounded-3xl p-10 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-devices-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Multi-Device Support</h3>
              <p className="text-base text-gray-400 leading-relaxed">
                Connect all your devices simultaneously. Whether you're on desktop, mobile, or tablet, enjoy seamless protection across your entire digital ecosystem.
              </p>
            </div>

            <div className="bg-[#1C1C1E] rounded-3xl p-10 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Lightning-Fast Speeds</h3>
              <p className="text-base text-gray-400 leading-relaxed">
                Experience blazing-fast connection speeds with our optimized server network. Stream, download, and browse without any lag or buffering delays.
              </p>
            </div>

            <div className="bg-[#1C1C1E] rounded-3xl p-10 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-infinity-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Unlimited Bandwidth</h3>
              <p className="text-base text-gray-400 leading-relaxed">
                No data caps, no throttling, no restrictions. Enjoy truly unlimited bandwidth for all your online activities without worrying about usage limits.
              </p>
            </div>

            <div className="bg-[#1C1C1E] rounded-3xl p-10 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-earth-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Global Server Network</h3>
              <p className="text-base text-gray-400 leading-relaxed">
                Access content from anywhere with our worldwide server network. Connect to servers in multiple countries for optimal performance and unrestricted access.
              </p>
            </div>

            <div className="bg-[#1C1C1E] rounded-3xl p-10 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Automatic Protection</h3>
              <p className="text-base text-gray-400 leading-relaxed">
                Stay protected automatically with our intelligent connection system. Your VPN activates instantly when you go online, ensuring continuous security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Contact */}
      <section id="pricing-contact" className="py-32 bg-gradient-to-br from-[#0F1729] to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-16">
            <div className="flex-1">
              <h2 className="text-5xl font-bold mb-16">Get In Touch</h2>
              
              <div className="space-y-10">
                <div className="flex items-start gap-5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-2xl text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">EMAIL</p>
                    <a href="mailto:info@lamadererasrl.com" className="text-lg font-medium hover:text-blue-500 transition-colors cursor-pointer">
                      info@lamadererasrl.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-2xl text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">PHONE</p>
                    <a href="tel:+14127753664" className="text-lg font-medium hover:text-blue-500 transition-colors cursor-pointer">
                      +1 412-775-3664
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-blue-500"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">ADDRESS</p>
                    <p className="text-lg font-medium leading-relaxed">
                      25 DE MAYO 411<br />
                      San Ramon de la Nueva Oran<br />
                      A4530ACI, Argentina
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 rounded-3xl border-2 border-blue-500 shadow-2xl shadow-blue-500/30 p-16">
                <p className="text-base text-blue-400 mb-4">VPN Service</p>
                <h3 className="text-4xl font-bold mb-8">Contact Us for<br />Enterprise Solutions</h3>
                
                <div className="h-px bg-white/20 mb-8"></div>
                
                <div className="space-y-5 mb-12">
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">Unlimited devices</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">Global server access</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">24/7 priority support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">Custom security solutions</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">Dedicated account manager</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">Advanced encryption protocols</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">Flexible billing options</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-check-line text-xl text-blue-500"></i>
                    <span className="text-lg">SLA guarantees</span>
                  </div>
                </div>

                <a 
                  href="mailto:info@lamadererasrl.com"
                  className="block w-full py-5 bg-blue-600 hover:bg-blue-700 text-white text-center font-semibold rounded-2xl transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50 whitespace-nowrap cursor-pointer"
                >
                  Request Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-32 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm text-blue-500 tracking-widest uppercase mb-5">SUPPORT</p>
          <h2 className="text-6xl font-bold mb-8">We're Here to Help</h2>
          <p className="text-xl text-gray-400 leading-relaxed mb-20 max-w-3xl mx-auto">
            Our dedicated support team is available around the clock to assist you with any questions or concerns about our VPN service.
          </p>

          <div className="grid grid-cols-3 gap-10">
            <div className="bg-white rounded-3xl p-12 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <i className="ri-book-open-line text-6xl text-blue-600 mb-7"></i>
              <h3 className="text-3xl font-bold text-black mb-4">Documentation</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Access our comprehensive knowledge base with detailed guides, tutorials, and troubleshooting resources to help you get the most out of our VPN service.
              </p>
              <a href="mailto:info@lamadererasrl.com" className="text-base text-blue-600 font-medium hover:underline cursor-pointer">
                Learn More →
              </a>
            </div>

            <div className="bg-white rounded-3xl p-12 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <i className="ri-mail-line text-6xl text-blue-600 mb-7"></i>
              <h3 className="text-3xl font-bold text-black mb-4">Email Support</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Send us your questions via email and our expert support team will respond promptly with detailed solutions tailored to your specific needs.
              </p>
              <a href="mailto:info@lamadererasrl.com" className="text-base text-blue-600 font-medium hover:underline cursor-pointer">
                Learn More →
              </a>
            </div>

            <div className="bg-white rounded-3xl p-12 shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
              <i className="ri-message-3-line text-6xl text-blue-600 mb-7"></i>
              <h3 className="text-3xl font-bold text-black mb-4">Live Chat</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Get instant assistance through our live chat support. Connect with our team in real-time for quick answers and immediate problem resolution.
              </p>
              <a href="mailto:info@lamadererasrl.com" className="text-base text-blue-600 font-medium hover:underline cursor-pointer">
                Learn More →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-16 mb-24">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/e1cd9872-53a1-4a52-95de-ce140d99ae94.png" 
                  alt="LA MADERERA S.R.L." 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                Protecting your digital freedom with advanced VPN technology. Secure, private, and unrestricted internet access for everyone.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase mb-6">STAY UPDATED</h4>
              <div className="relative mb-4">
                <input 
                  type="email" 
                  placeholder="Enter your email"
                  className="w-full bg-transparent border-b border-gray-800 pb-3 text-sm text-gray-300 placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                />
                <button className="absolute right-0 bottom-3 text-blue-500 hover:text-blue-400 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
              <p className="text-xs text-gray-600">
                We respect your privacy. <a href="#" className="text-blue-500 hover:underline cursor-pointer">Learn more</a>
              </p>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase mb-6">COMPANY</h4>
              <div className="space-y-5">
                <button onClick={() => scrollToSection('about')} className="block text-base text-gray-400 hover:text-blue-500 transition-colors cursor-pointer">
                  About Us
                </button>
                <button onClick={() => scrollToSection('vpn-service')} className="block text-base text-gray-400 hover:text-blue-500 transition-colors cursor-pointer">
                  Features
                </button>
                <button onClick={() => scrollToSection('pricing-contact')} className="block text-base text-gray-400 hover:text-blue-500 transition-colors cursor-pointer">
                  Contact
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold uppercase mb-6">LEGAL</h4>
              <div className="space-y-5">
                <a href="#" className="block text-base text-gray-400 hover:text-blue-500 transition-colors cursor-pointer">
                  Privacy Policy
                </a>
                <a href="#" className="block text-base text-gray-400 hover:text-blue-500 transition-colors cursor-pointer">
                  Terms of Service
                </a>
                <a href="#" className="block text-base text-gray-400 hover:text-blue-500 transition-colors cursor-pointer">
                  Cookie Policy
                </a>
              </div>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="text-9xl font-black text-[#E8DCC4]/40 tracking-tight" style={{ letterSpacing: '-2px' }}>
              LA MADERERA
            </h2>
          </div>

          <div className="pt-8 border-t border-gray-900">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                © 2025 LA MADERERA S.R.L. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-blue-500 transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
